/* eslint-disable no-underscore-dangle */
import { builder } from '@builder.io/react';
import { useRef } from 'react';
import Image from 'next/legacy/image';

import { themeDark } from 'react-scale/components/elements/header/header-themes';

import Header from 'react-scale/components/elements/header';
import Footer from 'react-scale/components/elements/footer';
import SectionTitle from 'react-scale/components/elements/section-title';
import Presenters from 'react-scale/components/pages/interviews/presenters';
import TryBanner from 'react-scale/components/sections/cta/simple-centered';
import Section from 'react-scale/components/elements/section';

import QuoteHighlight from 'react-scale/components/pages/interviews/quote-highlight';
import ChatHighlight from 'react-scale/components/pages/interviews/chat-highlight';
import Hero from 'react-scale/components/pages/interviews/hero';
import Video from 'react-scale/components/elements/video';
import handleError from 'react-scale/lib/handle-error';

const getAuthor = async authorId => {
  let author;
  try {
    const builderAuthor = await builder
      .get('hosts', {
        options: { includeRefs: true, noTargeting: true },
        omit: 'data.blocks',
        query: { id: authorId }
      })
      .promise();
    author = builderAuthor;
  } catch (e) {
    handleError(e);
  }

  return author;
};

const InterviewPage = ({ interview }) => {
  const reactPlayerRef = useRef();

  const updatePlayerOnSeek = goTo => {
    reactPlayerRef.current.seekTo(goTo);
  };

  let facebookCard;
  let twitterCard;

  if (interview?.facebookCard) {
    facebookCard = interview.facebookCard;
  } else if (interview?.videoPoster) {
    facebookCard = interview?.videoPoster;
  } else if (interview?.image) {
    facebookCard = interview?.image;
  }

  if (interview?.twitterCard) {
    twitterCard = interview.twitterCard;
  } else if (interview?.videoPoster) {
    twitterCard = interview?.videoPoster;
  } else if (interview?.image) {
    twitterCard = interview?.image;
  }

  const interviewPresenters = interview?.hosts?.map(host => ({
    picture: host?.host?.value.data.avatar,
    name: host?.host?.value.data.name,
    positions: host?.host?.value.data.titlesList?.split(',').map(t => ({ title: t })),
    content: host?.host?.value.data.description,
    social: {
      twitter: host?.host?.value.data.twitter,
      linkedin: host?.host?.value.data.linkedin
    }
  }));

  return (
    <div className="text-white bg-black">
      <div className="select-none absolute w-full h-[776px]">
        <div className="relative w-full h-full">
          <Image
            src="/static/images/pages/transformx/speakers-top-colors.svg"
            layout="fill"
            objectFit="cover"
          />
        </div>
      </div>

      <Header
        title={interview?.title}
        description={interview?.description}
        canonical={`https://scale.com/interviews/${interview?.slug}`}
        ogImg={facebookCard}
        twImg={twitterCard}
        theme={themeDark}
        borderColor="border-white/10"
        border
      />

      <div className="relative space-y-20 lg:space-y-32">
        <Section paddingTop={false} paddingBottom={false}>
          <Hero
            type="Interviews"
            publishedDate={interview?.publishedDate}
            title={interview?.title ?? ''}
          />
          <Video
            className="mt-12"
            width={1728}
            height={810}
            poster={interview?.videoPoster}
            url={interview?.videoUrl}
            fixOnScroll
            ref={reactPlayerRef}
          />
        </Section>
        <Section className="max-w-3xl mx-auto text-center" paddingTop={false} paddingBottom={false}>
          <SectionTitle
            overtitle="About this event"
            title={interview?.title ?? ''}
            subtitle={interview?.description ?? ''}
          />
        </Section>
        <Section container={false} paddingTop={false} paddingBottom={false}>
          {interviewPresenters && <Presenters presenters={interviewPresenters} />}
        </Section>
        {interview?.highlights?.length > 0 && (
          <Section paddingTop={false} paddingBottom={false}>
            <h3 className="mx-auto text-4xl lg:text-5xl max-w-max text-transform-gradient font-aeonik">
              Interview Highlights
            </h3>
            <div className="space-y-20 lg:space-y-32 mt-14">
              {interview?.highlights?.map(highlight => {
                // eslint-disable-next-line no-underscore-dangle
                if (highlight?.value.data.isQuote) {
                  return (
                    <QuoteHighlight
                      quote={highlight?.value.data.quote}
                      author={highlight?.value.data.author?.name}
                    />
                  );
                }
                if (highlight?.value.data.chat) {
                  return (
                    <ChatHighlight
                      updatePlayerTime={updatePlayerOnSeek}
                      time={highlight?.value.data.second}
                      chat={highlight?.value.data.chat}
                    />
                  );
                }
              })}
            </div>
          </Section>
        )}
      </div>

      <div className="mt-28">
        <TryBanner border />
      </div>

      <Footer
        textColor="text-neutral-400"
        textHoverColor="text-white"
        borderColor="border-neutral-800"
        border
      />
    </div>
  );
};

export async function getStaticPaths() {
  const pages = await builder.getAll('interview', {
    options: { noTargeting: true },
    omit: 'data.blocks',
    limit: 100
  });

  return {
    paths: pages.map(page => ({ params: { slug: `${page.data.slug}` } })),
    fallback: true
  };
}

// We get the interview chat highlight
const getChatData = async chat => {
  if (!chat) return null;

  const chatData = await Promise.all(
    chat.map(async content => ({
      ...content,
      // we fetch for the authors from Builder, because the CMS only returns the first level references and we have to go deeper
      author: await getAuthor(content.author?.id).then(res => res.data)
    }))
  );

  return chatData;
};

// Wee get the interview highlight and return the same values, except for
// the chat data and the author, because we have to fetch the authors from Builder with the returned id
const getInterviewHighlight = async interview => {
  const cleanHighlight = await Promise.all(
    interview.data.highlights.map(async highlight => ({
      ...highlight.highlight,
      value: {
        ...highlight.highlight.value,
        data: {
          ...highlight.highlight.value.data,
          chat: await getChatData(highlight.highlight.value.data.chat),
          author: highlight.highlight.value.data.author?.id
            ? await getAuthor(highlight.highlight.value.data.author?.id).then(res => res.data)
            : null
        }
      }
    }))
  );

  return cleanHighlight;
};

export async function getStaticProps({ params }) {
  let interview;

  try {
    const builderInterview = await builder
      .get('interview', {
        options: { includeRefs: true, noTargeting: true },
        omit: 'data.blocks',
        query: { 'data.slug': params.slug }
      })
      .promise();
    // this workaround is because Builder only returns the first level references, so we have to go and get the hosts
    interview = {
      ...builderInterview,
      data: {
        ...builderInterview.data,
        highlights: await getInterviewHighlight(builderInterview)
      }
    };
  } catch (error) {
    handleError(error);
  }

  return {
    props: {
      interview: interview?.data,
      revalidate: 1
    }
  };
}

export default InterviewPage;
